<template>
  <div v-if="testPlatform === 'video' && !participantData && this.initialLoad">
    <div v-if="localTestState">Video is processing...</div>
    <div v-else class="process-video-button" @click="handleProcessVideo">
      Process Video Test
    </div>
  </div>
  <div v-else-if="this.initialLoad">
    <div class="blocks">
      <div>
        <div class="header-stats">
          <div v-if="dropdownOptions">
            <bg-select
              @change="handleSelectChange"
              :options="dropdownOptions"
              :value="dropdownValue"
            ></bg-select>
          </div>
        </div>

        <video-player
          v-if="participantData"
          :data="participantData"
          :key="renderKey"
          :sections="sections"
          :customControls="true"
          :testId="testId"
          @pressStart="handlePressStart"
          @pressEnd="handlePressEnd"
          @currentSectionChange="handleCurrentSectionChange"
          @timeUpdate="handleTimeUpdate"
          :getRef="setVideoRef"
          :active-section-id="currentSection ? currentSection.id : null"
          :testPlatform="testPlatform"
        ></video-player>

        <div class="sections-sidebar">
          <div class="sections-sidebar__content">
            <div v-if="currentSection">
              <div class="sections-sidebar__title">Section</div>
              <div class="sections-sidebar__action-buttons">
                <div
                  class="
                    sections-sidebar__action-buttons__button
                    sections-sidebar__action-buttons__button--remove
                  "
                  @click="handleRemoveSectionClick"
                >
                  <bg-icon class="sidebar__button__icon" name="trashcan" />
                </div>
              </div>
              <bg-loader
                :dark="true"
                style="position: relative; float: right; top: -23px; left: 0"
                v-if="savingInProgress"
              ></bg-loader>
              <transition name="fade">
                <div class="checkmark" v-if="savingSuccessfull"></div>
              </transition>
              <div class="errors" v-if="errors.length > 0">
                <div
                  class="errors--error"
                  v-for="(error, index) in errors"
                  :key="index"
                >
                  {{ error.message }}
                </div>
              </div>
              <div class="sections-sidebar__small-title">Section name:</div>
              <advanced-select
                prompt="Select sections"
                add-prompt="Add section"
                form-prompt="Section name"
                entity="section"
                :options="labelsOptions"
                :selected="selectedLabel"
                :multiple="false"
                @create="handleCreateLabel"
                @update="handleUpdateLabel"
                @delete="handleDeleteLabel"
                @change="handleLabelSelect"
              >
              </advanced-select>
              <div class="sections-sidebar__small-title">
                Section start & end time:
              </div>
              <div class="sections-sidebar__time-wrapper">
                <label style="margin-right: 10px">Start: </label>
                <editable-content
                  v-if="this.currentSection"
                  v-model="timeStart"
                  :uniqueKey="`s${this.currentSection.id}`"
                  :rows="1"
                ></editable-content>
              </div>
              <div class="sections-sidebar__time-wrapper">
                <label style="margin-right: 10px">End: </label>
                <editable-content
                  v-if="this.currentSection"
                  v-model="timeEnd"
                  :uniqueKey="`e${this.currentSection.id}`"
                  :rows="1"
                ></editable-content>
              </div>
            </div>
            <div v-else>
              <div class="sections-sidebar__title">Create Mode</div>
              <bg-loader
                :dark="true"
                style="position: relative; float: right; top: -23px; left: 0"
                v-if="savingInProgress"
              ></bg-loader>
              <transition name="fade">
                <div class="checkmark" v-if="savingSuccessfull"></div>
              </transition>
              <div class="errors">
                <div class="errors--info" v-if="creatingSection">
                  There is currently a section in making. Please click End or ]
                  to finish creating a section :)
                </div>
              </div>
              <div class="sections-sidebar__create-mode-info">
                Unleash your creativity in our brand new Create Mode™ :<br /><br />
                [ - start new Section / end Section that is currently being
                created and immediately start new one<br /><br />
                ] - end Section that is currently being created<br />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="assistant"
      v-if="testPlatform === 'desktop' || testPlatform === 'mobile'"
    >
      <h2 v-bg-typography="'h2'">Sections assistant</h2>
      <p v-if="batchSections.length === 0">
        <b>Select and match. </b> Please select a page title, and then select
        all urls that will receive that same page title. Then hit 'Add'. Keep
        doing so untill there are no urls left. Then hit 'Create sections'
        <b>Watch it: </b> using the assistant will remove all existing sections
        for this participant!
      </p>

      <p style="margin-top: 10px" v-if="batchSections.length > 0">
        We currently matched <b>{{ batchSections.length }} URL's</b>. Click
        'Create sections' to create the sections. <b>Watch it: </b> using the
        assistant will remove all existing sections for this participant!
      </p>
      <bg-loader :dark="true" v-if="isLoading"></bg-loader>
      <div class="pageTitle" v-if="!isLoading">
        <advanced-select
          id="advanceSelect"
          prompt="Select sections"
          add-prompt="Add section"
          form-prompt="Section name"
          entity="section"
          :options="labelsOptions"
          :selected="pageTitle"
          :multiple="false"
          @create="handleCreateLabel"
          @update="handleUpdateLabel"
          @delete="handleDeleteLabel"
          @change="handleSelect"
        >
        </advanced-select>

        <div class="buttons">
          <bg-button
            variant="primary bigbutton"
            text="Add"
            @click="addSections"
            style="margin-right: 10px"
          />
          <bg-button
            variant="secondary bigbutton"
            :text="`Create sections`"
            @click="uploadAll"
          />
        </div>
      </div>
      <List
        v-show="!isLoading"
        :headers="list.headers"
        :values="list.values"
        :actions="list.actions"
        :data="cleanedUrls"
        :fractions="list.fractions"
        @change="getChanges"
        selectMode="true"
        ref="listOfUrls"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "./styles.scss";

.bigbutton {
  height: 46px;
  min-width: 191px;
  font-size: 12pt;
}

.checkmark {
  transform: rotate(45deg);
  height: 20px;
  width: 12px;
  border-bottom: 3px solid #00deab;
  border-right: 3px solid #00deab;
  float: right;
  position: relative;
  top: -37px;
  right: 10px;
}
.process-video-button {
  text-align: center;
  font-weight: bold;
  width: 191px;
  height: 40px;
  border-radius: 6px;
  padding-top: 10px;
  cursor: pointer;
  background-color: #0b0b3d;
  color: white;
  margin-bottom: 20px;
  &:active {
    opacity: 0.7;
  }
}
.blocks {
  display: flex;
  flex-direction: column;
}

.assistant {
  color: #0b0b3d;
  margin-bottom: 200px;
  padding-top: 50px;
  border-top: 1px solid #c4c4c4;
  max-width: 1000px;
}

#advanceSelect {
  min-width: 300px;
  top: 0;
  left: 0;
}

.pageTitle {
  margin-top: 45px;
  margin-bottom: 25px;
  display: flex;
  max-width: 900px;
  gap: 30px;
  position: relative;
  z-index: 3;
  min-height: 80px;
}
</style>

<script>
import moment from "moment";
import BgSelect from "@/components/bg-select";
import VideoPlayer from "@/components/video-player";
import AdvancedSelect from "@/components/advanced-select";
import BgIcon from "@/components/bg-icon";
import API from "@/api";
import EditableContent from "@/components/editable-content";
import BgLoader from "@/components/bg-loader";
import BgTypography from "@/directives/bg-typography";
import BgField from "@/components/bg-field";
import BgInput from "@/components/bg-input";
import BgButton from "@/components/bg-button";
import List from "@/components/list";
import check from "@/components/bg-icon/icons/check";

export default {
  name: "researchParticipantSections",
  components: {
    BgSelect,
    VideoPlayer,
    AdvancedSelect,
    EditableContent,
    BgIcon,
    BgLoader,
    BgInput,
    BgField,
    BgButton,
    List,
  },
  directives: {
    BgTypography,
  },
  props: {
    handleSelectChange: {
      type: Function,
    },
    dropdownOptions: {
      type: Array,
    },
    dropdownValue: {
      type: Object,
    },
    testId: {
      type: Number,
      optional: true,
    },
    data: {
      type: Object,
      required: true,
    },
    testPlatform: {
      type: String,
      required: true,
    },
    testInternalState: {
      type: String,
      optional: true,
    },
  },
  computed: {
    cleanedUrls: function() {
      const data = [];
      let previousDataPoint = {
        url: "",
        start: null,
        end: null,
      };
      this.data.mice.forEach((obj) => {
        if (obj.url && !obj.url.includes("localhost")) {
          let newUrl = "";

          if (obj.url.includes("?")) {
            newUrl = obj.url.split("?")[0];
          } else {
            newUrl = obj.url;
          }

          newUrl = decodeURIComponent(newUrl);

          if (newUrl != previousDataPoint.url) {
            let newSection = {
              url: newUrl,
              start: obj.timestamp,
              end: obj.timestamp,
              duration: 0,
            };
            data.push(newSection);
          } else if (newUrl === previousDataPoint.url) {
            data[data.length - 1].end = obj.timestamp;
            data[data.length - 1].duration = Math.floor(
              (data[data.length - 1].end - data[data.length - 1].start) / 1000
            );
          }
          previousDataPoint.url = newUrl;
        }
      });

      let previousEnd = 0;

      data.forEach((obj) => {
        obj.previousEnd = previousEnd;
        previousEnd = obj.end;
      });

      return data;
    },
  },
  methods: {
    forceRerender() {
      this.renderKey += 1;
    },
    handleSelect(_type, ids) {
      this.pageTitle = [];
      this.pageTitle.push(ids[0]);
    },
    existingSections() {
      this.savingInProgress = false;
      const child = this.$refs.listOfUrls;

      this.cleanedUrls.forEach((obj, index) => {
        const existing = this.labels.find((label) => {
          return label.urls.includes(obj.url);
        });
        if (existing) {
          const section = {
            timestamp_start: this.cleanedUrls[index].start,
            timestamp_end: this.cleanedUrls[index].end,
            label_id: existing.id,
            participant_id: this.data.real_id,
            id: null,
            previousEnd: this.cleanedUrls[index].previousEnd,
            url: this.cleanedUrls[index].url,
          };
          if (section.label_id) {
            this.batchSections.push(section);
          }
          child.activate(index);
        }
      });

      child.hideActivated();
      child.resetActivated();
    },
    addSections() {
      let previousEnd = null;
      this.selectedPages.forEach((el) => {
        const section = {
          timestamp_start: this.cleanedUrls[el].start,
          timestamp_end: this.cleanedUrls[el].end,
          label_id: this.pageTitle[0],
          participant_id: this.data.real_id,
          id: null,
          previousEnd: this.cleanedUrls[el].previousEnd,
          url: this.cleanedUrls[el].url,
        };
        if (section.label_id) {
          this.batchSections.push(section);
        }
      });

      const child = this.$refs.listOfUrls;
      child.hideActivated();
      child.resetActivated();
    },
    async uploadAll() {
      //removing all existing sections
      const participantData = await API.test.getParticipant(
        this.$route.params.id,
        this.$route.params.participantId
      );
      participantData.sections.forEach(async (sec) => {
        const deleteRes = await API.test.deleteSection(sec.id);
      });
      this.sections = [];
      let updatedLabels = this.labels;

      //checking sections to combine
      const sections = this.batchSections;
      const finalSections = [];

      sections.forEach((section) => {
        if (
          !updatedLabels
            .find((el) => el.id === section.label_id)
            .urls.includes(section.url)
        ) {
          updatedLabels
            .find((el) => el.id === section.label_id)
            .urls.push(section.url);
        }

        const adjacent = sections.findIndex(check);
        function check(obj) {
          return obj.timestamp_end === section.previousEnd;
        }
        let sameLabel = false;
        if (adjacent >= 0) {
          sameLabel = section.label_id === sections[adjacent].label_id;
        }

        if (adjacent >= 0 && sameLabel) {
          sections[adjacent].timestamp_end = section.timestamp_end;
        } else {
          finalSections.push(section);
        }
      });
      //uploading sections
      await finalSections.forEach(async (obj) => {
        const res = await API.test.createSection(obj);
      });

      //updating labels
      this.updateLabels(updatedLabels);

      // re render video player
      await API.test
        .getParticipant(this.testId, this.data.id)
        .then((res) => {
          this.batchSections = [];
          res.sections.forEach((el) => this.sections.push(el));
        })
        .then(() => {
          this.forceRerender();
        });
      this.existingSections();
    },
    updateLabels(labels) {
      labels.forEach(async (label) => {
        const res = await API.test.updateLabel(this.testId, label.id, label);
      });
    },
    getChanges(val) {
      if (val.action === "Activated") {
        this.selectedPages = [];
        val.item.forEach((el) => {
          this.selectedPages.push(el);
        });
      } else if (val.action === "View") {
        window.open(val.item.url, "_blank");
      }
    },
    async getSections() {
      const existingSections = [];
      const res = await API.test.getTest(this.testId);
      res.participants.forEach(async (obj) => {
        const participantData = await API.test.getParticipant(
          this.$route.params.id,
          obj.id
        );
        participantData.sections.forEach((el) => existingSections.push(el));
      });
    },
    setVideoRef(ref) {
      this.videoRef = ref;
    },
    getLabels() {
      API.test
        .getPages(this.testId)
        .then((rawLabels) => {
          this.prepareLabelsForSelect(rawLabels.labels);
          this.labels = rawLabels.labels;
        })
        .then(() => this.existingSections())
        .then(() => {
          this.isLoading = false;
        });
    },
    prepareLabelsForSelect(rawLabels) {
      const labelsForSelect = rawLabels.map((label) => {
        return { name: label.name, value: label.id };
      });
      this.labelsOptions = labelsForSelect;
    },
    selectLabel(section) {
      this.selectedLabel = [];
      if (section.label_id) {
        this.selectedLabel.push(section.label_id);
      }
    },
    convertToMmSs(newVal) {
      const s = parseInt(newVal % 60);
      const m = parseInt((newVal / 60) % 60);
      const time = moment()
        .seconds(s)
        .minutes(m)
        .format("mm:ss");
      return time;
    },
    switchSection(section) {
      const creatingSectionIndex = this.sections.findIndex(
        (s) => s === this.creatingSection
      );
      if (creatingSectionIndex !== -1) {
        this.sections.splice(creatingSectionIndex, 1);
      }
      this.errors = [];
      if (this.currentSection === section) {
        this.currentSection = null;
        return;
      }
      this.validateSection(section);
      this.currentSection = section;
      if (section.timestamp_start) {
        this.timeStart = this.convertToMmSs(
          (section.timestamp_start - this.participantData.started_at) / 1000
        );
      } else {
        this.timeStart = this.convertToMmSs(
          (this.participantData.started_at - this.participantData.started_at) /
            1000
        );
      }
      if (section.timestamp_end) {
        this.timeEnd = this.convertToMmSs(
          (section.timestamp_end - this.participantData.started_at) / 1000
        );
      } else {
        this.timeEnd = this.convertToMmSs(
          (this.participantData.started_at - this.participantData.started_at) /
            1000
        );
      }
      this.selectLabel(section);
    },
    setTime(type, time) {
      if (this.currentSection) {
        this.updateCurrentSectionTimes(type, time);
      } else {
        this.updateCreateMode(type, time);
      }
    },
    updateCurrentSectionTimes(type, time) {
      if (type === "start") {
        this.currentSection.timestamp_start =
          this.participantData.started_at + Math.floor(time * 1000);
        this.attemptFixOverlap(this.currentSection);
        this.timeStart = this.convertToMmSs(
          (this.currentSection.timestamp_start -
            this.participantData.started_at) /
            1000
        );
      } else if (type === "end") {
        this.currentSection.timestamp_end =
          this.participantData.started_at + Math.floor(time * 1000);
        this.attemptFixOverlap(this.currentSection);
        this.timeEnd = this.convertToMmSs(
          (this.currentSection.timestamp_end -
            this.participantData.started_at) /
            1000
        );
      }
      this.saveSection(this.currentSection);
    },
    updateCreateMode(type, time) {
      if (this.creatingSection) {
        if (type === "end") {
          this.creatingSection.timestamp_end = this.convertToParticipantTime(
            time
          );
          this.saveSection(this.creatingSection);
          this.creatingSection = null;
          return;
        }
        this.creatingSection.timestamp_end = this.convertToParticipantTime(
          time
        );
        this.saveSection(this.creatingSection);
        this.switchToNewSection(time);
        return;
      } else {
        if (type === "end") return;
        this.switchToNewSection(time);
        return;
      }
    },
    convertToParticipantTime(time) {
      return this.participantData.started_at + Math.floor(time * 1000);
    },
    switchToNewSection(time) {
      const newSection = { ...this.emptySection };
      newSection.timestamp_start = this.convertToParticipantTime(time) + 1;
      this.creatingSection = newSection;
      this.sections.push(newSection);
    },
    async saveSection(section) {
      if (section.timestamp_start && section.timestamp_end) {
        if (!section.participant_id)
          section.participant_id = this.participantData.real_id;
        this.attemptFixOverlap(section);
        this.validateSection(section);
        if (this.errors.length > 0) return;

        if (section.id) {
          this.savingInProgress = true;
          await API.test.updateSection(section.id, section);
          this.savingInProgress = false;
          this.savingSuccessfull = true;
          setTimeout(() => {
            this.savingSuccessfull = false;
          }, 500);
        } else {
          this.savingInProgress = true;
          await API.test.createSection(section);
          this.savingInProgress = false;
          this.savingSuccessfull = true;
          setTimeout(() => {
            this.savingSuccessfull = false;
          }, 500);
        }

        const participantData = await API.test.getParticipant(
          this.$route.params.id,
          this.$route.params.participantId
        );
        const updatedSection = participantData.sections.find(
          (s) => s.timestamp_start === section.timestamp_start
        );
        section.id = updatedSection.id;
      }
    },
    attemptFixOverlap(currentSection) {
      this.sections.forEach((section) => {
        if (
          section.timestamp_start < currentSection.timestamp_start &&
          section.timestamp_end > currentSection.timestamp_start
        ) {
          currentSection.timestamp_start = section.timestamp_end + 1;
        }
        if (
          section.timestamp_start < currentSection.timestamp_end &&
          section.timestamp_end > currentSection.timestamp_end
        ) {
          currentSection.timestamp_end = section.timestamp_start - 1;
        }
      });
    },
    validateSection(currentSection) {
      this.errors = [];
      this.validateEndBeforeStart(currentSection);
      this.validateCoverUp(currentSection);
    },
    validateEndBeforeStart(currentSection) {
      if (currentSection.timestamp_end < currentSection.timestamp_start) {
        this.addError(
          "endBeforeStart",
          "It seems like the beginning of the section is later than the end of the section. Please fix it cause we don't know what you mean by this. Love you, bye! <3"
        );
      }
    },
    validateCoverUp(currentSection) {
      this.sections.forEach((section) => {
        if (
          currentSection !== section &&
          currentSection.timestamp_start <= section.timestamp_start &&
          currentSection.timestamp_end >= section.timestamp_end
        ) {
          this.addError(
            "coverUp",
            "Your current section is covering up another section. Please consider aligning these two sections correctly. Love you, bye! <3"
          );
        }
      });
    },
    handleCurrentSectionChange(section) {
      this.videoRef.currentTime =
        (section.timestamp_start - this.participantData.started_at) / 1000;
      this.switchSection(section);
    },
    addError(type, message) {
      this.errors.push({
        type,
        message,
      });
    },
    async handleRemoveSectionClick() {
      if (this.currentSection.id) {
        const sectionIndex = this.sections.findIndex(
          (section) => section === this.currentSection
        );
        this.sections.splice(sectionIndex, 1);
        this.savingInProgress = true;
        await API.test.deleteSection(this.currentSection.id);
        this.currentSection = null;
        this.savingInProgress = false;
        this.savingSuccessfull = true;
        setTimeout(() => {
          this.savingSuccessfull = false;
        }, 500);
      } else {
        const sectionIndex = this.sections.findIndex(
          (section) => section === this.currentSection
        );
        this.sections.splice(sectionIndex, 1);
        this.currentSection = null;
      }
    },
    async handleCreateLabel(_type, label) {
      await API.test.createLabel(this.testId, { name: label.name });
      await this.getLabels();
    },
    async handleUpdateLabel(_type, label) {
      await API.test.updateLabel(this.testId, label.value, {
        name: label.name,
      });
      await this.getLabels();
    },
    handleLabelSelect(_type, ids) {
      this.selectedLabel = ids;
      this.currentSection.label_id = ids[0];
      this.saveSection(this.currentSection);
    },
    async handleDeleteLabel(_type, label) {
      await API.test.deleteLabel(this.testId, label.value);
      await this.getLabels();
      API.test
        .getParticipant(this.$route.params.id, this.$route.params.participantId)
        .then((res) => {
          this.sections = res.sections;
          let section;
          if (this.currentSection.id) {
            section = res.sections.find(
              (section) => section.id === this.currentSection.id
            );
          } else {
            section = res.sections.find(
              (section) =>
                section.timestamp_start === this.currentSection.timestamp_start
            );
          }
          this.switchSection(section);
        });
    },
    handleTimeUpdate(time) {
      this.currentVideoTime = time;
    },
    handleLeftBracket() {
      this.handlePressStart(this.currentVideoTime);
    },
    handleRightBracket() {
      this.handlePressEnd(this.currentVideoTime);
    },
    handlePressStart(time) {
      this.videoRef.play();
      this.setTime("start", time);
    },
    handlePressEnd(time) {
      this.videoRef.pause();
      this.setTime("end", time);
    },
    handleKeyUp(event) {
      if (event.code === "BracketLeft") {
        this.handleLeftBracket();
      }
      if (event.code === "BracketRight") {
        this.handleRightBracket();
      }
    },
    handleProcessVideo() {
      API.test.processVideoTest(this.testId);
      this.localTestState = "video_processing";
      this.checkInternalStateAndLoadData(this.localTestState);
    },
    async checkInternalStateAndLoadData(state) {
      if (state) {
        setTimeout(async () => {
          const result = await API.test.getTest(this.testId);
          this.checkInternalStateAndLoadData(result.internal_state);
        }, 5000);
      } else {
        try {
          this.participantData = await API.test.getVideoParticipant(
            this.testId
          );
          this.sections = this.participantData.sections;
          this.localTestState = state;
        } catch (e) {
          console.log(e);
        }
      }
    },
  },
  data() {
    return {
      batchSections: [],
      labelsOptions: [],
      labels: null,
      isLoading: true,
      list: {
        actions: ["View"],
        headers: ["URL", "Duration (s)"],
        values: ["url", "duration"],
        fractions: [9, 1],
      },
      selectedLabel: [],
      timeStart: "00:00",
      timeEnd: "00:00",
      currentSection: null,
      emptySection: {
        timestamp_start: null,
        timestamp_end: null,
        label_id: null,
        participant_id: this.data.real_id,
        id: null,
      },
      errors: [],
      renderKey: 0,
      sections: [],
      savingInProgress: false,
      savingSuccessfull: false,
      currentVideoTime: 0,
      creatingSection: false,
      videoRef: null,
      pageTitle: [],
      selectedPages: [],
      participantData: null,
      initialLoad: false,
      localTestState: null,
    };
  },
  async mounted() {
    if (this.testPlatform === "video") {
      this.localTestState = this.testInternalState;
      await this.checkInternalStateAndLoadData(this.localTestState);
    } else {
      this.participantData = this.data;
      this.sections = this.participantData.sections;
    }
    this.getLabels();
    this.initialLoad = true;
  },
  created() {
    window.addEventListener("keyup", this.handleKeyUp);
    this.getSections();
  },
  destroyed() {
    window.removeEventListener("keyup", this.handleKeyUp);
  },
};
</script>
